import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  ShieldCheck, Users, HardDrives, Bell, GoogleDriveLogo,
  Feather, SignOut, ArrowsClockwise, CurrencyInr, CheckCircle, MinusCircle,
} from "@phosphor-icons/react";

function humanBytes(b) {
  if (!b) return "0 B";
  const k = 1024, s = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(b) / Math.log(k)), s.length - 1);
  return (b / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1) + " " + s[i];
}

function StatCard({ Icon, label, value, sub, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className={`border rounded-lg p-5 bg-card ${accent ? "border-accent/40" : "border-border"}`}
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon size={16} weight="duotone" />
        <p className="text-xs uppercase tracking-[0.2em] font-semibold">{label}</p>
      </div>
      <p className={`font-serif text-4xl mt-3 leading-none ${accent ? "text-accent" : ""}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-2">{sub}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all"); // all | drive | reminders

  const load = async () => {
    try {
      const [s, u] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
      ]);
      setStats(s.data);
      setUsers(u.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login", { replace: true });
      else toast.error("Failed to load");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const logout = async () => {
    await api.post("/admin/logout");
    navigate("/admin/login", { replace: true });
  };

  const shown = users.filter((u) => {
    if (filter === "drive") return u.drive_connected;
    if (filter === "reminders") return u.reminder_enabled;
    return true;
  });

  if (loading || !stats) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="min-h-screen paper-grain" data-testid="admin-dashboard-page">
      <nav className="sticky top-0 z-30 backdrop-blur-md bg-background/85 border-b border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/admin" className="flex items-center gap-2" data-testid="admin-brand">
            <Feather size={22} weight="duotone" className="text-primary" />
            <span className="font-serif text-2xl tracking-tight">Antic</span>
            <span className="text-xs uppercase tracking-[0.3em] text-accent ml-3 border-l border-border pl-3">
              Admin
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-refresh-btn"
              variant="ghost"
              size="sm"
              onClick={() => { setRefreshing(true); load(); }}
              className="text-xs uppercase tracking-[0.2em]"
            >
              <ArrowsClockwise size={14} className={`mr-1 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              data-testid="admin-logout-btn"
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-xs uppercase tracking-[0.2em]"
            >
              <SignOut size={14} className="mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground flex items-center gap-2">
          <ShieldCheck size={14} weight="duotone" /> Restricted view
        </p>
        <h1 className="font-serif text-5xl lg:text-6xl font-light tracking-tighter mt-3 mb-10">
          The whole <span className="italic text-accent">room</span>, from above
        </h1>

        {/* Stats grid */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14" data-testid="admin-stats-grid">
          <StatCard
            testId="stat-users"
            Icon={Users}
            label="Total users"
            value={stats.total_users}
            sub={`${stats.new_users_7d} new this week`}
            accent
          />
          <StatCard
            testId="stat-storage"
            Icon={HardDrives}
            label="Storage used"
            value={humanBytes(stats.storage_used_bytes)}
            sub={`${stats.total_entries} total entries`}
          />
          <StatCard
            testId="stat-drive"
            Icon={GoogleDriveLogo}
            label="Drive connected"
            value={stats.drive_connected_users}
            sub={`of ${stats.total_users} users`}
          />
          <StatCard
            testId="stat-reminders"
            Icon={Bell}
            label="Reminders on"
            value={stats.reminder_enabled_users}
            sub={`of ${stats.total_users} users`}
          />
          <StatCard
            testId="stat-active-subs"
            Icon={ArrowsClockwise}
            label="Active subs"
            value={stats.active_subscriptions}
            sub={`${stats.cancelled_subscriptions} cancelled`}
          />
          <StatCard
            testId="stat-revenue"
            Icon={CurrencyInr}
            label="Revenue"
            value={`₹${(stats.total_revenue_inr || 0).toFixed(0)}`}
            sub={`${stats.total_charges} charges`}
            accent
          />
          <StatCard
            testId="stat-extra-gb"
            Icon={HardDrives}
            label="Extra GB sold"
            value={stats.storage_used_gb_purchased}
            sub="one-time top-ups"
          />
          <StatCard
            testId="stat-avg"
            Icon={Feather}
            label="Avg entries"
            value={stats.total_users ? (stats.total_entries / stats.total_users).toFixed(1) : "0"}
            sub="per user"
          />
        </section>

        {/* Users table */}
        <section>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <h2 className="font-serif text-3xl">Users</h2>
            <div
              className="inline-flex items-center gap-1 p-1 bg-secondary rounded-full border border-border"
              data-testid="user-filter"
            >
              <button
                data-testid="filter-all"
                onClick={() => setFilter("all")}
                className={`px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-semibold rounded-full transition-colors ${
                  filter === "all" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                All · {users.length}
              </button>
              <button
                data-testid="filter-drive"
                onClick={() => setFilter("drive")}
                className={`px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-semibold rounded-full transition-colors ${
                  filter === "drive" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Drive · {stats.drive_connected_users}
              </button>
              <button
                data-testid="filter-reminders"
                onClick={() => setFilter("reminders")}
                className={`px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-semibold rounded-full transition-colors ${
                  filter === "reminders" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Reminders · {stats.reminder_enabled_users}
              </button>
            </div>
          </div>

          <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="users-table">
            <div className="grid grid-cols-[1fr_130px_100px_100px_120px_120px] gap-3 px-5 py-3 bg-secondary/40 text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
              <span>User</span>
              <span className="text-right">Storage</span>
              <span className="text-right">Entries</span>
              <span className="text-right">Subs</span>
              <span className="text-center">Drive</span>
              <span className="text-right">Joined</span>
            </div>
            {shown.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-muted-foreground">No users match.</p>
            ) : (
              <ul>
                {shown.map((u) => (
                  <li
                    key={u.user_id}
                    data-testid={`admin-user-${u.user_id}`}
                    className="grid grid-cols-[1fr_130px_100px_100px_120px_120px] gap-3 px-5 py-4 items-center border-t border-border"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-8 h-8 rounded-full border border-border" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-semibold">
                          {(u.name || u.email)[0]?.toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{u.name || "—"}</p>
                        <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                      </div>
                    </div>
                    <span className="text-right font-mono text-xs">{humanBytes(u.storage_used_bytes || 0)}</span>
                    <span className="text-right font-serif text-lg">{u.entries_count || 0}</span>
                    <span className="text-right font-serif text-lg">{u.active_subs || 0}</span>
                    <span className="text-center">
                      {u.drive_connected ? (
                        <span className="inline-flex items-center gap-1 text-primary text-xs uppercase tracking-[0.15em]">
                          <CheckCircle size={14} weight="duotone" /> Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-muted-foreground text-xs uppercase tracking-[0.15em]">
                          <MinusCircle size={14} /> No
                        </span>
                      )}
                    </span>
                    <span className="text-right text-xs text-muted-foreground">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
